/**
 * SiteMapAdmin — CRUD dialog for warehouses, gate readers, and bulk-import
 * from GeoJSON / KML site GIS. Every entity carries GPS so the map pin is
 * exactly where the physical asset stands. Warehouses here are the SINGLE
 * source of truth used by both Inventory and Site Map.
 */
import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { X, Plus, Save, Trash2, Upload, MapPin, Warehouse as WhIcon,
         Radio, Crosshair, Loader2 } from 'lucide-react';

const C = { primary: '#1E4E8C', accent: '#F26B1F', slate: '#0F172A',
             slateMuted: '#64748B', border: 'rgba(15,23,42,0.10)',
             success: '#10B981', warn: '#F59E0B', danger: '#EF4444' };

const WH_TYPES = ['BIN', 'RACK', 'SITE_STATION', 'STATION_RACK', 'GENERAL', 'PIPING', 'STRUCTURAL', 'LAYDOWN'];
const IP_TYPES = ['WH_EXIT_GATE', 'WH_ENTRY_GATE',
                   'STATION_ENTRY_GATE', 'STATION_EXIT_GATE',
                   'SITE_ENTRY_GATE', 'SITE_EXIT_GATE',
                   'IQC_DESK', 'BIN_PUTAWAY', 'RESERVATION', 'ISSUE_COUNTER',
                   'POINT_OF_USE', 'INSTALLATION', 'RETURN_PICKUP', 'RECEIPT_COUNTER'];

function TextInput({ label, value, onChange, placeholder, testid, type = 'text' }) {
  return (
    <div className="mb-2">
      <label className="text-[9px] uppercase font-semibold" style={{ color: C.slateMuted }}>{label}</label>
      <input type={type} value={value || ''} onChange={e => onChange(e.target.value)}
        placeholder={placeholder} data-testid={testid}
        className="w-full px-2 py-1 rounded border text-[11px]"
        style={{ borderColor: C.border, background: '#fff' }} />
    </div>
  );
}

function WarehouseForm({ projectId, existing, initial, onSaved, onCancel, onPickGps }) {
  const [f, setF] = useState(existing || initial || { location: '', label: '', type: 'GENERAL',
                                              gpsLat: '', gpsLng: '', address: '', manager: '',
                                              rackCapacity: '', capacityUnit: 'kg' });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF({ ...f, [k]: v });
  const submit = async () => {
    if (!f.location?.trim()) return toast.error('Location code is required');
    setSaving(true);
    try {
      const body = {
        location: f.location.trim(), label: f.label, type: f.type,
        gpsLat: f.gpsLat === '' ? null : Number(f.gpsLat),
        gpsLng: f.gpsLng === '' ? null : Number(f.gpsLng),
        address: f.address, manager: f.manager, description: f.description,
        rackCapacity: f.rackCapacity === '' || f.rackCapacity == null ? null : Number(f.rackCapacity),
        capacityUnit: f.capacityUnit || null,
        // Rack ↔ parent hierarchy (BIN → RACK, SITE_STATION → STATION_RACK)
        parentBin: f.parentBin || null,
        parentBinCode: f.parentBinCode || null,
        parentStation: f.parentStation || null,
        parentStationCode: f.parentStationCode || null,
        discipline: f.discipline || null,
        disciplineCode: f.disciplineCode || null,
        disciplineColor: f.disciplineColor || null,
      };
      if (existing?.id) {
        await api.patch(`/v1/projects/${projectId}/mtx/warehouses/${existing.id}`, body);
      } else {
        await api.post(`/v1/projects/${projectId}/mtx/warehouses`, body);
      }
      toast.success(`Warehouse ${existing ? 'updated' : 'created'}`);
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };
  const isRack = f.type === 'RACK' || f.type === 'STATION_RACK';
  return (
    <div className="p-3 rounded border bg-white" style={{ borderColor: C.border }} data-testid="wh-form">
      <div className="flex items-center gap-2 mb-3">
        <WhIcon className="w-4 h-4" style={{ color: C.success }} />
        <div className="text-sm font-bold">{existing ? 'Edit' : 'Add'} {isRack ? 'Rack' : 'Warehouse'}
          {isRack && (f.parentBin || f.parentStation) && (
            <span className="ml-2 text-[9.5px] font-mono uppercase" style={{ color: C.slateMuted }}>
              in {f.parentBin || f.parentStation}
            </span>
          )}
        </div>
      </div>
      <TextInput label="Location Code *" value={f.location} onChange={v => set('location', v)}
        placeholder="e.g. WH-A · Bay 3 · General" testid="wh-form-location" />
      <TextInput label="Display Label" value={f.label} onChange={v => set('label', v)}
        placeholder="Shown on map & drawer" testid="wh-form-label" />
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[9px] uppercase font-semibold" style={{ color: C.slateMuted }}>Type</label>
          <select value={f.type} onChange={e => set('type', e.target.value)}
            data-testid="wh-form-type"
            className="w-full px-2 py-1 rounded border text-[11px]" style={{ borderColor: C.border }}>
            {WH_TYPES.map(t => <option key={t}>{t}</option>)}
          </select>
        </div>
        <TextInput label="Manager" value={f.manager} onChange={v => set('manager', v)}
          testid="wh-form-manager" />
      </div>
      {/* Rack Capacity — feeds the fill-bar on Material Tracking bins */}
      <div className="grid grid-cols-3 gap-2 p-2 rounded-lg mt-2"
        style={{ background: '#F0FDF4', border: `1px dashed ${C.success}` }}>
        <div className="col-span-2">
          <label className="text-[9px] uppercase font-semibold flex items-center gap-1" style={{ color: C.success }}>
            <span>📏</span> Rack Capacity
          </label>
          <input type="number" value={f.rackCapacity ?? ''} onChange={e => set('rackCapacity', e.target.value)}
            placeholder="e.g. 15000 — leave blank for auto-derive"
            data-testid="wh-form-rack-capacity"
            className="w-full px-2 py-1 rounded border text-[11px]" style={{ borderColor: C.border }} />
          <div className="text-[9px] mt-0.5" style={{ color: C.slateMuted }}>
            Balance ÷ this capacity drives the fill-bar. Blank = auto (accepted × 1.5).
          </div>
        </div>
        <div>
          <label className="text-[9px] uppercase font-semibold" style={{ color: C.success }}>Unit</label>
          <select value={f.capacityUnit || 'kg'} onChange={e => set('capacityUnit', e.target.value)}
            data-testid="wh-form-capacity-unit"
            className="w-full px-2 py-1 rounded border text-[11px]" style={{ borderColor: C.border }}>
            {['kg', 'm', 'NOS', 'pallets', 'm³', 't'].map(u => <option key={u}>{u}</option>)}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 relative">
        <TextInput label="GPS Latitude" value={f.gpsLat} onChange={v => set('gpsLat', v)}
          placeholder="24.4600" testid="wh-form-lat" type="number" />
        <TextInput label="GPS Longitude" value={f.gpsLng} onChange={v => set('gpsLng', v)}
          placeholder="54.6600" testid="wh-form-lng" type="number" />
        <button onClick={() => onPickGps?.(({ lat, lng }) => { set('gpsLat', lat.toFixed(6)); set('gpsLng', lng.toFixed(6)); })}
          className="absolute right-0 top-0 text-[9px] px-1 rounded font-semibold flex items-center gap-1"
          style={{ background: '#EEF2FF', color: C.primary }}
          data-testid="wh-form-pick-map">
          <Crosshair className="w-3 h-3" /> Pick on Map
        </button>
      </div>
      <TextInput label="Address" value={f.address} onChange={v => set('address', v)}
        testid="wh-form-address" />
      <div className="flex justify-end gap-2 mt-2">
        <Button size="sm" variant="ghost" onClick={onCancel} data-testid="wh-form-cancel">Cancel</Button>
        <Button size="sm" onClick={submit} disabled={saving}
          data-testid="wh-form-save"
          style={{ background: C.primary, color: '#fff' }}>
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Save className="w-3.5 h-3.5 mr-1" /> Save</>}
        </Button>
      </div>
    </div>
  );
}

function GateReaderForm({ projectId, warehouses, existing, onSaved, onCancel, onPickGps }) {
  const [f, setF] = useState(existing || { type: 'WH_EXIT_GATE', label: '',
                                              warehouseId: '', gpsLat: '', gpsLng: '',
                                              readerType: 'RFID', deviceIds: [] });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF({ ...f, [k]: v });
  const submit = async () => {
    if (!f.type) return toast.error('Type is required');
    setSaving(true);
    try {
      const body = {
        type: f.type, label: f.label, warehouseId: f.warehouseId || null,
        gpsLat: f.gpsLat === '' ? null : Number(f.gpsLat),
        gpsLng: f.gpsLng === '' ? null : Number(f.gpsLng),
        readerType: f.readerType, deviceIds: f.deviceIds || [],
        coverageRadiusMeters: f.coverageRadiusMeters || null,
      };
      if (existing?.id) {
        await api.patch(`/v1/projects/${projectId}/mtx/gate-readers/${existing.id}`, body);
      } else {
        await api.post(`/v1/projects/${projectId}/mtx/gate-readers`, body);
      }
      toast.success(`Gate reader ${existing ? 'updated' : 'created'}`);
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };
  return (
    <div className="p-3 rounded border bg-white" style={{ borderColor: C.border }} data-testid="gr-form">
      <div className="flex items-center gap-2 mb-3">
        <Radio className="w-4 h-4" style={{ color: C.info }} />
        <div className="text-sm font-bold">{existing ? 'Edit' : 'Add'} Gate Reader / Scan Station</div>
      </div>
      <div className="mb-2">
        <label className="text-[9px] uppercase font-semibold" style={{ color: C.slateMuted }}>Interface Type *</label>
        <select value={f.type} onChange={e => set('type', e.target.value)}
          data-testid="gr-form-type"
          className="w-full px-2 py-1 rounded border text-[11px]" style={{ borderColor: C.border }}>
          {IP_TYPES.map(t => <option key={t}>{t}</option>)}
        </select>
      </div>
      <TextInput label="Label" value={f.label} onChange={v => set('label', v)}
        testid="gr-form-label" />
      <div className="mb-2">
        <label className="text-[9px] uppercase font-semibold" style={{ color: C.slateMuted }}>Linked Warehouse</label>
        <select value={f.warehouseId || ''} onChange={e => set('warehouseId', e.target.value)}
          data-testid="gr-form-wh"
          className="w-full px-2 py-1 rounded border text-[11px]" style={{ borderColor: C.border }}>
          <option value="">— none —</option>
          {warehouses.map(w => <option key={w.id} value={w.id}>{w.label || w.location}</option>)}
        </select>
      </div>
      <div className="mb-2">
        <label className="text-[9px] uppercase font-semibold" style={{ color: C.slateMuted }}>Reader Hardware</label>
        <select value={f.readerType} onChange={e => set('readerType', e.target.value)}
          data-testid="gr-form-hw"
          className="w-full px-2 py-1 rounded border text-[11px]" style={{ borderColor: C.border }}>
          <option>RFID</option><option>QR</option><option>HYBRID</option>
        </select>
      </div>
      <div className="grid grid-cols-2 gap-2 relative">
        <TextInput label="GPS Latitude" value={f.gpsLat} onChange={v => set('gpsLat', v)}
          testid="gr-form-lat" type="number" />
        <TextInput label="GPS Longitude" value={f.gpsLng} onChange={v => set('gpsLng', v)}
          testid="gr-form-lng" type="number" />
        <button onClick={() => onPickGps?.(({ lat, lng }) => { set('gpsLat', lat.toFixed(6)); set('gpsLng', lng.toFixed(6)); })}
          className="absolute right-0 top-0 text-[9px] px-1 rounded font-semibold flex items-center gap-1"
          style={{ background: '#EEF2FF', color: C.primary }}
          data-testid="gr-form-pick-map">
          <Crosshair className="w-3 h-3" /> Pick on Map
        </button>
      </div>
      <TextInput label="Coverage Radius (metres)" value={f.coverageRadiusMeters || ''}
        onChange={v => set('coverageRadiusMeters', v === '' ? null : Number(v))}
        placeholder="e.g. 25 for a fixed portal · 8 for a handheld cone"
        testid="gr-form-radius" type="number" />
      <div className="flex justify-end gap-2 mt-2">
        <Button size="sm" variant="ghost" onClick={onCancel} data-testid="gr-form-cancel">Cancel</Button>
        <Button size="sm" onClick={submit} disabled={saving}
          data-testid="gr-form-save"
          style={{ background: C.primary, color: '#fff' }}>
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Save className="w-3.5 h-3.5 mr-1" /> Save</>}
        </Button>
      </div>
    </div>
  );
}

export default function SiteMapAdmin({ projectId, onClose, onSaved, onPickGps }) {
  const [tab, setTab] = useState('warehouses');
  const [warehouses, setWarehouses] = useState([]);
  const [readers, setReaders] = useState([]);
  const [editingWh, setEditingWh] = useState(null);
  const [addingWh, setAddingWh] = useState(false);
  const [addingRackFor, setAddingRackFor] = useState(null);   // parent bin/station preset for rack form
  const [editingGr, setEditingGr] = useState(null);
  const [barrierFor, setBarrierFor] = useState(null);   // GateReader whose barrier we're configuring
  const [addingGr, setAddingGr] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const load = useCallback(async () => {
    const [{ data: w }, { data: g }] = await Promise.all([
      api.get(`/v1/projects/${projectId}/mtx/warehouses`),
      api.get(`/v1/projects/${projectId}/mtx/interface-points`),
    ]);
    setWarehouses(w.warehouses || []);
    setReaders((g.interfacePoints || []).filter(ip =>
      ['WH_EXIT_GATE', 'SITE_ENTRY_GATE', 'SITE_EXIT_GATE', 'WH_ENTRY_GATE',
       'STATION_ENTRY_GATE', 'STATION_EXIT_GATE'].includes(ip.type)));
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const delWh = async (id) => {
    if (!window.confirm('Delete this warehouse? Historical MCI links will remain.')) return;
    try { await api.delete(`/v1/projects/${projectId}/mtx/warehouses/${id}`);
      toast.success('Deleted'); load(); onSaved?.(); }
    catch { toast.error('Delete failed'); }
  };
  const delGr = async (id) => {
    if (!window.confirm('Delete this gate reader?')) return;
    try { await api.delete(`/v1/projects/${projectId}/mtx/gate-readers/${id}`);
      toast.success('Deleted'); load(); onSaved?.(); }
    catch { toast.error('Delete failed'); }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-stretch justify-end" style={{ background: 'rgba(15,23,42,0.35)' }} onClick={onClose}>
      <div className="w-[520px] bg-white shadow-2xl overflow-y-auto p-4"
        onClick={e => e.stopPropagation()} data-testid="site-map-admin">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-bold" style={{ color: C.slate }}>
            <MapPin className="w-4 h-4 inline mr-1" style={{ color: C.accent }} />
            Site GIS Admin
          </div>
          <button onClick={onClose} data-testid="admin-close"><X className="w-4 h-4" /></button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b mb-2" style={{ borderColor: C.border }}>
          {['warehouses', 'readers'].map(k => (
            <button key={k} onClick={() => setTab(k)}
              data-testid={`admin-tab-${k}`}
              className="px-3 py-1.5 text-[10px] uppercase font-semibold"
              style={{ color: tab === k ? C.primary : C.slateMuted,
                        borderBottom: tab === k ? `2px solid ${C.primary}` : 'none' }}>
              {k}
            </button>
          ))}
        </div>

        {tab === 'warehouses' && (
          <div>
            <div className="flex gap-2 mb-2">
              <Button size="sm" onClick={() => { setAddingWh(true); setEditingWh(null); }}
                data-testid="admin-add-wh"
                style={{ background: C.success, color: '#fff' }}>
                <Plus className="w-3.5 h-3.5 mr-1" /> Add Warehouse
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setImportOpen(true)}
                data-testid="admin-import-gis">
                <Upload className="w-3.5 h-3.5 mr-1" /> Import GIS
              </Button>
            </div>
            {addingWh && (
              <WarehouseForm projectId={projectId}
                onSaved={() => { setAddingWh(false); load(); onSaved?.(); }}
                onCancel={() => setAddingWh(false)}
                onPickGps={onPickGps} />
            )}
            {addingRackFor && (
              <WarehouseForm projectId={projectId} initial={addingRackFor}
                onSaved={() => { setAddingRackFor(null); load(); onSaved?.(); }}
                onCancel={() => setAddingRackFor(null)}
                onPickGps={onPickGps} />
            )}
            {editingWh && (
              <WarehouseForm projectId={projectId} existing={editingWh}
                onSaved={() => { setEditingWh(null); load(); onSaved?.(); }}
                onCancel={() => setEditingWh(null)}
                onPickGps={onPickGps} />
            )}
            <div className="mt-2">
              {(() => {
                // Group racks under their parent bin so the admin list
                // reflects the site-map hierarchy (Bin → nested Racks)
                // — matches screenshot 1's mental model. Site Stations
                // render as a separate section below the warehouse bins.
                const bins = warehouses.filter(w => w.type === 'BIN');
                const siteStations = warehouses.filter(w => w.type === 'SITE_STATION');
                const racksByParent = new Map();
                warehouses.filter(w => w.type === 'RACK').forEach(r => {
                  const key = r.parentBin || r.parentBinCode || '__ORPHAN__';
                  if (!racksByParent.has(key)) racksByParent.set(key, []);
                  racksByParent.get(key).push(r);
                });
                const stationRacksByParent = new Map();
                warehouses.filter(w => w.type === 'STATION_RACK').forEach(r => {
                  const key = r.parentStation || r.parentStationCode || '__ORPHAN__';
                  if (!stationRacksByParent.has(key)) stationRacksByParent.set(key, []);
                  stationRacksByParent.get(key).push(r);
                });
                const other = warehouses.filter(w =>
                  w.type !== 'BIN' && w.type !== 'RACK' &&
                  w.type !== 'SITE_STATION' && w.type !== 'STATION_RACK');
                if (bins.length === 0 && siteStations.length === 0 && other.length === 0) {
                  return <div className="p-3 text-center text-[10px] italic" style={{ color: C.slateMuted }}>
                    No warehouses yet — add one or import GIS.
                  </div>;
                }
                const RackRow = ({ r }) => {
                  const fill = r.fillPct ?? 0;
                  const stat = r.fillStatus || 'OK';
                  const gaugeColor = stat === 'DANGER' ? C.danger
                                   : stat === 'WARN'   ? C.warn
                                   :                     C.success;
                  const capText = r.rackCapacity
                    ? `${r.balanceStock || 0} / ${r.rackCapacity} ${r.capacityUnit || ''}`
                    : `${r.balanceStock || 0} (auto-capped)`;
                  return (
                    <div className="pl-6 pr-2 py-1.5 border-b bg-slate-50/40 flex items-center gap-2"
                      style={{ borderColor: C.border }} data-testid={`wh-row-${r.id}`}>
                      <span className="inline-block w-1 h-1 rounded-full"
                        style={{ background: r.disciplineColor || C.slateMuted }} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <div className="text-[10.5px] font-bold" style={{ color: C.slate }}>
                            <span className="font-mono text-[9.5px] mr-1" style={{ color: r.disciplineColor || C.slateMuted }}>{r.disciplineCode || '—'}</span>
                            {r.discipline || r.label || r.location}
                          </div>
                          {stat !== 'OK' && (
                            <span className="text-[8.5px] px-1 py-[1px] rounded font-bold uppercase tracking-wider text-white"
                              style={{ background: gaugeColor }}
                              data-testid={`wh-fill-status-${r.id}`}>
                              {stat === 'DANGER' ? 'Overloaded' : 'Near Full'}
                            </span>
                          )}
                        </div>
                        <div className="text-[8.5px] font-mono truncate" style={{ color: C.slateMuted }}>{r.location}</div>
                        <div className="flex items-center gap-1.5 mt-0.5" data-testid={`wh-fill-gauge-${r.id}`}>
                          <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: '#E2E8F0' }}>
                            <div className="h-full transition-all" style={{
                              width: `${Math.min(100, fill)}%`, background: gaugeColor,
                            }} />
                          </div>
                          <span className="text-[8.5px] font-mono tabular-nums font-bold" style={{ color: gaugeColor, minWidth: 32 }}>
                            {fill.toFixed(0)}%
                          </span>
                        </div>
                        <div className="text-[8px] mt-0.5" style={{ color: C.slateMuted }}>{capText}</div>
                      </div>
                      <button onClick={() => { setEditingWh(r); setAddingWh(false); }}
                        className="text-[9px] px-1.5 py-0.5 rounded font-semibold"
                        style={{ background: '#EEF2FF', color: C.primary }}
                        data-testid={`wh-edit-${r.id}`}>Edit</button>
                      <button onClick={() => delWh(r.id)}
                        className="text-[9px] px-1.5 py-0.5 rounded"
                        style={{ background: '#FEE2E2', color: C.danger }}
                        data-testid={`wh-del-${r.id}`}><Trash2 className="w-3 h-3" /></button>
                    </div>
                  );
                };
                return (
                  <>
                    {bins.map(b => {
                      const kids = (racksByParent.get(b.id) || racksByParent.get(b.parentBinCode) || [])
                        .sort((a, c) => (a.disciplineCode || '').localeCompare(c.disciplineCode || ''));
                      const isOwner = b.location?.includes('Owner');
                      const binColor = isOwner ? '#1E40AF' : '#6D28D9';
                      const totalStock = b.iqcOkStock ?? kids.reduce((s, r) => s + (r.iqcOkStock || 0), 0);
                      const binFill = b.fillPct ?? 0;
                      const binStat = b.fillStatus || 'OK';
                      const binGauge = binStat === 'DANGER' ? C.danger
                                     : binStat === 'WARN'   ? C.warn
                                     :                        C.success;
                      const overloadedRacks = kids.filter(r => r.fillStatus === 'DANGER').length;
                      const warnRacks = kids.filter(r => r.fillStatus === 'WARN').length;
                      return (
                        <div key={b.id} className="mb-2 rounded-lg overflow-hidden border"
                          style={{ borderColor: `${binColor}44` }}>
                          <div className="px-2 py-1.5 flex items-center gap-2"
                            style={{ background: `${binColor}12`, borderBottom: `1px solid ${binColor}33` }}
                            data-testid={`wh-row-${b.id}`}>
                            <span style={{ display:'inline-block',width:12,height:12,background:binColor,opacity:.55,borderRadius:3 }} />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <div className="text-[11px] font-bold" style={{ color: binColor }}>
                                  {b.label || b.location}
                                  <span className="ml-1.5 text-[8.5px] font-mono uppercase tracking-wider"
                                    style={{ color: C.slateMuted }}>BIN · {kids.length} racks</span>
                                </div>
                                {overloadedRacks > 0 && (
                                  <span className="text-[8.5px] px-1 py-[1px] rounded font-bold uppercase tracking-wider text-white"
                                    style={{ background: C.danger }}>{overloadedRacks} overloaded</span>
                                )}
                                {warnRacks > 0 && overloadedRacks === 0 && (
                                  <span className="text-[8.5px] px-1 py-[1px] rounded font-bold uppercase tracking-wider text-white"
                                    style={{ background: C.warn }}>{warnRacks} near full</span>
                                )}
                              </div>
                              <div className="text-[9px]" style={{ color: C.slateMuted }}>
                                Total Stock: <b style={{ color: C.success }}>{totalStock}</b> ·
                                GPS: {b.gpsLat && b.gpsLng ? `${b.gpsLat.toFixed(4)}, ${b.gpsLng.toFixed(4)}` : '—'}
                              </div>
                              <div className="flex items-center gap-1.5 mt-0.5" data-testid={`wh-fill-gauge-${b.id}`}>
                                <div className="flex-1 h-1 rounded-full overflow-hidden" style={{ background: '#E2E8F0' }}>
                                  <div className="h-full transition-all" style={{
                                    width: `${Math.min(100, binFill)}%`, background: binGauge,
                                  }} />
                                </div>
                                <span className="text-[8.5px] font-mono tabular-nums font-bold" style={{ color: binGauge, minWidth: 32 }}>
                                  {binFill.toFixed(0)}%
                                </span>
                              </div>
                            </div>
                            <button onClick={() => setAddingRackFor({
                                type: 'RACK',
                                parentBin: b.location, parentBinCode: b.id,
                                gpsLat: b.gpsLat ?? '', gpsLng: b.gpsLng ?? '',
                                location: `${(b.location || '').split(' ')[0]} · NEW-RACK · `,
                                label: '', rackCapacity: '', capacityUnit: 'kg',
                              })}
                              className="text-[9px] px-1.5 py-0.5 rounded font-semibold flex items-center gap-0.5"
                              style={{ background: `${C.success}22`, color: C.success }}
                              data-testid={`wh-add-rack-${b.id}`}>
                              <Plus className="w-3 h-3" /> Rack
                            </button>
                            <button onClick={() => { setEditingWh(b); setAddingWh(false); }}
                              className="text-[9px] px-1.5 py-0.5 rounded font-semibold"
                              style={{ background: '#EEF2FF', color: C.primary }}
                              data-testid={`wh-edit-${b.id}`}>Edit</button>
                            <button onClick={() => delWh(b.id)}
                              className="text-[9px] px-1.5 py-0.5 rounded"
                              style={{ background: '#FEE2E2', color: C.danger }}
                              data-testid={`wh-del-${b.id}`}><Trash2 className="w-3 h-3" /></button>
                          </div>
                          {kids.map(r => <RackRow key={r.id} r={r} />)}
                          {kids.length === 0 && (
                            <div className="p-2 text-[9.5px] italic text-center" style={{ color: C.slateMuted }}>
                              No racks under this bin yet.
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {siteStations.length > 0 && (
                      <div className="mt-3">
                        <div className="text-[9.5px] uppercase font-bold mb-1 flex items-center gap-1"
                          style={{ color: '#0F766E' }}>
                          <MapPin className="w-3 h-3" /> Site Stations · On-site Inventory
                        </div>
                        {siteStations.map(s => {
                          const kids = (stationRacksByParent.get(s.location) || stationRacksByParent.get(s.parentStationCode) || [])
                            .sort((a, c) => (a.disciplineCode || '').localeCompare(c.disciplineCode || ''));
                          const totalStock = s.iqcOkStock ?? kids.reduce((sum, r) => sum + (r.iqcOkStock || 0), 0);
                          const sFill = s.fillPct ?? 0;
                          const sStat = s.fillStatus || 'OK';
                          const sGauge = sStat === 'DANGER' ? C.danger : sStat === 'WARN' ? C.warn : C.success;
                          return (
                            <div key={s.id} className="mb-2 rounded-lg overflow-hidden border"
                              style={{ borderColor: '#0F766E44' }}>
                              <div className="px-2 py-1.5 flex items-center gap-2"
                                style={{ background: '#0F766E12', borderBottom: '1px solid #0F766E33' }}
                                data-testid={`wh-row-${s.id}`}>
                                <span style={{ display:'inline-block',width:12,height:12,background:'#0F766E',opacity:.55,borderRadius:3 }} />
                                <div className="flex-1 min-w-0">
                                  <div className="text-[11px] font-bold" style={{ color: '#0F766E' }}>
                                    {s.label || s.location}
                                    <span className="ml-1.5 text-[8.5px] font-mono uppercase tracking-wider"
                                      style={{ color: C.slateMuted }}>STATION · {kids.length} racks</span>
                                  </div>
                                  <div className="text-[9px]" style={{ color: C.slateMuted }}>
                                    Stock: <b style={{ color: C.success }}>{totalStock}</b> ·
                                    GPS: {s.gpsLat && s.gpsLng ? `${s.gpsLat.toFixed(4)}, ${s.gpsLng.toFixed(4)}` : '—'}
                                  </div>
                                  <div className="flex items-center gap-1.5 mt-0.5" data-testid={`wh-fill-gauge-${s.id}`}>
                                    <div className="flex-1 h-1 rounded-full overflow-hidden" style={{ background: '#E2E8F0' }}>
                                      <div className="h-full transition-all" style={{
                                        width: `${Math.min(100, sFill)}%`, background: sGauge,
                                      }} />
                                    </div>
                                    <span className="text-[8.5px] font-mono tabular-nums font-bold" style={{ color: sGauge, minWidth: 32 }}>
                                      {sFill.toFixed(0)}%
                                    </span>
                                  </div>
                                </div>
                                <button onClick={() => setAddingRackFor({
                                    type: 'STATION_RACK',
                                    parentStation: s.location, parentStationCode: s.id,
                                    gpsLat: s.gpsLat ?? '', gpsLng: s.gpsLng ?? '',
                                    location: `${(s.location || '').split(' ')[0]} · NEW-RACK · `,
                                    label: '', rackCapacity: '', capacityUnit: 'kg',
                                  })}
                                  className="text-[9px] px-1.5 py-0.5 rounded font-semibold flex items-center gap-0.5"
                                  style={{ background: '#0F766E22', color: '#0F766E' }}
                                  data-testid={`wh-add-rack-${s.id}`}>
                                  <Plus className="w-3 h-3" /> Rack
                                </button>
                                <button onClick={() => { setEditingWh(s); setAddingWh(false); }}
                                  className="text-[9px] px-1.5 py-0.5 rounded font-semibold"
                                  style={{ background: '#EEF2FF', color: C.primary }}
                                  data-testid={`wh-edit-${s.id}`}>Edit</button>
                                <button onClick={() => delWh(s.id)}
                                  className="text-[9px] px-1.5 py-0.5 rounded"
                                  style={{ background: '#FEE2E2', color: C.danger }}
                                  data-testid={`wh-del-${s.id}`}><Trash2 className="w-3 h-3" /></button>
                              </div>
                              {kids.map(r => <RackRow key={r.id} r={r} />)}
                              {kids.length === 0 && (
                                <div className="p-2 text-[9.5px] italic text-center" style={{ color: C.slateMuted }}>
                                  No racks in this station yet.
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {other.length > 0 && (
                      <div className="mt-2 rounded border overflow-hidden" style={{ borderColor: C.border }}>
                        <div className="px-2 py-1 text-[9px] uppercase font-semibold" style={{ color: C.slateMuted, background: '#F1F5F9' }}>Un-classified</div>
                        {other.map(w => (
                          <div key={w.id} className="p-2 border-t flex items-center gap-2"
                            style={{ borderColor: C.border }} data-testid={`wh-row-${w.id}`}>
                            <div className="flex-1">
                              <div className="text-[11px] font-bold" style={{ color: C.slate }}>{w.label || w.location}</div>
                              <div className="text-[9px]" style={{ color: C.slateMuted }}>
                                Stock: <b style={{ color: C.success }}>{w.iqcOkStock || 0}</b> · Type: {w.type}
                              </div>
                            </div>
                            <button onClick={() => { setEditingWh(w); setAddingWh(false); }}
                              className="text-[9px] px-1.5 py-0.5 rounded font-semibold"
                              style={{ background: '#EEF2FF', color: C.primary }}
                              data-testid={`wh-edit-${w.id}`}>Edit</button>
                            <button onClick={() => delWh(w.id)}
                              className="text-[9px] px-1.5 py-0.5 rounded"
                              style={{ background: '#FEE2E2', color: C.danger }}
                              data-testid={`wh-del-${w.id}`}><Trash2 className="w-3 h-3" /></button>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        )}

        {tab === 'readers' && (
          <div>
            <Button size="sm" onClick={() => { setAddingGr(true); setEditingGr(null); }}
              data-testid="admin-add-gr"
              style={{ background: C.info, color: '#fff' }}>
              <Plus className="w-3.5 h-3.5 mr-1" /> Add Scan Station
            </Button>
            {addingGr && (
              <GateReaderForm projectId={projectId} warehouses={warehouses}
                onSaved={() => { setAddingGr(false); load(); onSaved?.(); }}
                onCancel={() => setAddingGr(false)}
                onPickGps={onPickGps} />
            )}
            {editingGr && (
              <GateReaderForm projectId={projectId} warehouses={warehouses} existing={editingGr}
                onSaved={() => { setEditingGr(null); load(); onSaved?.(); }}
                onCancel={() => setEditingGr(null)}
                onPickGps={onPickGps} />
            )}
            <div className="mt-2">
              {readers.map(r => (
                <div key={r.id} className="p-2 rounded border mb-1 bg-white flex items-center gap-2"
                  style={{ borderColor: C.border }} data-testid={`gr-row-${r.id}`}>
                  <div className="flex-1">
                    <div className="text-[11px] font-bold" style={{ color: C.slate }}>{r.label || r.type}</div>
                    <div className="text-[9px] font-mono" style={{ color: C.slateMuted }}>{r.type} · {r.readerType || 'RFID'}</div>
                    <div className="text-[9px]" style={{ color: C.slateMuted }}>
                      Status: <b style={{ color: C.success }}>{r.status || 'ONLINE'}</b> ·
                      GPS: {r.gpsLat && r.gpsLng ? `${r.gpsLat.toFixed(4)}, ${r.gpsLng.toFixed(4)}` : '—'}
                    </div>
                  </div>
                  <button onClick={() => { setEditingGr(r); setAddingGr(false); }}
                    className="text-[9px] px-1.5 py-0.5 rounded font-semibold"
                    style={{ background: '#EEF2FF', color: C.primary }}
                    data-testid={`gr-edit-${r.id}`}>Edit</button>
                  <button onClick={() => setBarrierFor(r)}
                    className="text-[9px] px-1.5 py-0.5 rounded font-semibold flex items-center gap-0.5"
                    style={{ background: r.gateBarrier?.webhookUrl ? `${C.success}22` : '#F1F5F9',
                              color: r.gateBarrier?.webhookUrl ? C.success : C.slateMuted }}
                    title={r.gateBarrier?.webhookUrl ? `Webhook: ${r.gateBarrier.webhookUrl}` : 'Configure gate barrier'}
                    data-testid={`gr-barrier-${r.id}`}>
                    <span style={{ fontSize: 11 }}>{r.gateBarrier?.webhookUrl ? '🚧' : '⚙︎'}</span>
                    Barrier
                  </button>
                  <button onClick={() => delGr(r.id)}
                    className="text-[9px] px-1.5 py-0.5 rounded"
                    style={{ background: '#FEE2E2', color: C.danger }}
                    data-testid={`gr-del-${r.id}`}><Trash2 className="w-3 h-3" /></button>
                </div>
              ))}
              {readers.length === 0 && (
                <div className="p-3 text-center text-[10px] italic" style={{ color: C.slateMuted }}>
                  No gate readers yet.
                </div>
              )}
            </div>
          </div>
        )}

        {importOpen && (
          <GisImportDialog projectId={projectId}
            onClose={() => setImportOpen(false)}
            onImported={() => { setImportOpen(false); load(); onSaved?.(); }} />
        )}
        {barrierFor && (
          <GateBarrierDialog projectId={projectId} reader={barrierFor}
            onClose={() => setBarrierFor(null)}
            onSaved={() => { setBarrierFor(null); load(); onSaved?.(); }} />
        )}
      </div>
    </div>
  );
}

function GateBarrierDialog({ projectId, reader, onClose, onSaved }) {
  const cfg = reader?.gateBarrier || {};
  const [webhookUrl, setWebhookUrl] = useState(cfg.webhookUrl || '');
  const [method, setMethod] = useState(cfg.method || 'POST');
  const [payloadTemplate, setPayloadTemplate] = useState(cfg.payloadTemplate ||
    `{"action":"OPEN","ip":"${reader.id}","unit":"{materialUnitId}"}`);
  const [headersRaw, setHeadersRaw] = useState(
    Object.keys(cfg.headers || {}).map(k => `${k}: ${cfg.headers[k]}`).join('\n'));
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const parseHeaders = () => {
    const h = {};
    (headersRaw || '').split('\n').forEach(line => {
      const [k, ...rest] = line.split(':');
      if (k && rest.length) h[k.trim()] = rest.join(':').trim();
    });
    return h;
  };
  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/v1/projects/${projectId}/mtx/gate-barrier/config`, {
        ipId: reader.id, webhookUrl: webhookUrl.trim(), method,
        headers: parseHeaders(), payloadTemplate,
      });
      toast.success('Gate barrier webhook saved');
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };
  const testFire = async () => {
    setTesting(true);
    try {
      // Save first, then trigger manually
      await api.post(`/v1/projects/${projectId}/mtx/gate-barrier/config`, {
        ipId: reader.id, webhookUrl: webhookUrl.trim(), method,
        headers: parseHeaders(), payloadTemplate,
      });
      const { data } = await api.post(
        `/v1/projects/${projectId}/mtx/gate-barrier/${reader.id}/trigger`);
      if (data.ok) toast.success(`Barrier fired · ${data.status}`);
      else toast.error(`Fire failed · ${data.error || data.status}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Fire failed'); }
    finally { setTesting(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: 'rgba(15,23,42,0.55)' }} onClick={onClose}>
      <div className="w-[500px] max-w-full bg-white rounded-lg p-4"
        onClick={e => e.stopPropagation()} data-testid="gate-barrier-dialog">
        <div className="flex items-center gap-2 mb-3">
          <span style={{ fontSize: 16 }}>🚧</span>
          <div className="text-sm font-bold">Gate Barrier · {reader.label || reader.type}</div>
          <button onClick={onClose} className="ml-auto"><X className="w-3.5 h-3.5" /></button>
        </div>
        <div className="text-[10px] mb-3" style={{ color: C.slateMuted }}>
          Configure the HTTP webhook that fires when a valid scan lands at this gate.
          The relay will open the physical barrier automatically. Placeholders
          <code className="mx-1 px-1 rounded bg-slate-100">{'{materialUnitId}'}</code> and
          <code className="mx-1 px-1 rounded bg-slate-100">{'{ipId}'}</code> can be used in the payload.
        </div>
        <div className="space-y-2">
          <div>
            <label className="text-[9px] uppercase font-semibold" style={{ color: C.slateMuted }}>Webhook URL</label>
            <input value={webhookUrl} onChange={e => setWebhookUrl(e.target.value)}
              placeholder="https://gate-controller.local/open"
              className="w-full px-2 py-1.5 rounded border text-[11px] font-mono"
              style={{ borderColor: C.border }}
              data-testid="barrier-webhook-url" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-[9px] uppercase font-semibold" style={{ color: C.slateMuted }}>Method</label>
              <select value={method} onChange={e => setMethod(e.target.value)}
                className="w-full px-2 py-1.5 rounded border text-[11px]"
                style={{ borderColor: C.border }}
                data-testid="barrier-method">
                {['POST','PUT','GET'].map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-[9px] uppercase font-semibold" style={{ color: C.slateMuted }}>Headers (one per line: Key: Value)</label>
            <textarea value={headersRaw} onChange={e => setHeadersRaw(e.target.value)}
              rows={2}
              placeholder="Authorization: Bearer xxx"
              className="w-full px-2 py-1.5 rounded border text-[10.5px] font-mono"
              style={{ borderColor: C.border }}
              data-testid="barrier-headers" />
          </div>
          <div>
            <label className="text-[9px] uppercase font-semibold" style={{ color: C.slateMuted }}>Payload Template</label>
            <textarea value={payloadTemplate} onChange={e => setPayloadTemplate(e.target.value)}
              rows={3}
              className="w-full px-2 py-1.5 rounded border text-[10.5px] font-mono"
              style={{ borderColor: C.border }}
              data-testid="barrier-payload" />
          </div>
        </div>
        <div className="flex items-center gap-2 mt-3">
          <button onClick={save} disabled={saving || !webhookUrl}
            className="px-3 py-1.5 rounded text-[11px] font-bold disabled:opacity-40"
            style={{ background: C.primary, color: '#fff' }}
            data-testid="barrier-save">
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button onClick={testFire} disabled={testing || !webhookUrl}
            className="px-3 py-1.5 rounded text-[11px] font-bold disabled:opacity-40"
            style={{ background: C.accent, color: '#fff' }}
            data-testid="barrier-test-fire">
            {testing ? 'Firing…' : 'Save + Test Fire'}
          </button>
          <button onClick={onClose} className="px-3 py-1.5 rounded text-[11px]"
            style={{ background: '#F1F5F9', color: C.slate }}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function GisImportDialog({ projectId, onClose, onImported }) {
  const [tab, setTab] = useState('shp');   // 'shp' | 'geojson'
  const [text, setText] = useState('');
  const [nameField, setNameField] = useState('name');
  const [labelField, setLabelField] = useState('name');
  const [busy, setBusy] = useState(false);
  const [shp, setShp] = useState({ shp: null, dbf: null, shx: null });

  const onFile = async (f) => {
    if (!f) return;
    setText(await f.text());
    toast.success(`Loaded ${f.name}`);
  };
  const onShpFiles = (files) => {
    const out = { shp: null, dbf: null, shx: null };
    Array.from(files || []).forEach(f => {
      const ext = f.name.toLowerCase().split('.').pop();
      if (['shp','dbf','shx'].includes(ext)) out[ext] = f;
    });
    setShp(out);
  };
  const toB64 = (file) => new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(String(r.result).split(',')[1] || '');
    r.onerror = rej;
    r.readAsDataURL(file);
  });

  const submit = async () => {
    setBusy(true);
    try {
      if (tab === 'shp') {
        if (!shp.shp || !shp.dbf) {
          toast.error('.shp and .dbf are required (.shx optional)');
          setBusy(false); return;
        }
        const [shpB64, dbfB64, shxB64] = await Promise.all([
          toB64(shp.shp), toB64(shp.dbf),
          shp.shx ? toB64(shp.shx) : Promise.resolve(null),
        ]);
        const { data } = await api.post(
          `/v1/projects/${projectId}/mtx/warehouses/import-shp`,
          { shpBase64: shpB64, dbfBase64: dbfB64, shxBase64: shxB64,
            propertyMap: { nameField, labelField } });
        toast.success(`Imported ${data.imported} warehouse(s) from SHP`);
      } else {
        if (!text.trim()) { toast.error('Paste or upload a GeoJSON payload'); setBusy(false); return; }
        const { data } = await api.post(
          `/v1/projects/${projectId}/mtx/warehouses/import-gis`,
          { format: 'geojson', payload: text,
            propertyMap: { nameField, labelField } });
        toast.success(`Imported ${data.imported} warehouse(s) from GeoJSON`);
      }
      onImported?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Import failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(15,23,42,0.55)' }} onClick={onClose}>
      <div className="w-[580px] bg-white rounded-lg p-4" onClick={e => e.stopPropagation()}
        data-testid="gis-import-dialog">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-bold">Bulk-Import Warehouses from GIS</div>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        <div className="flex gap-1 border-b mb-2" style={{ borderColor: C.border }}>
          {[{k:'shp',l:'SHP Bundle'},{k:'geojson',l:'GeoJSON'}].map(o => (
            <button key={o.k} onClick={() => setTab(o.k)}
              data-testid={`gis-tab-${o.k}`}
              className="px-3 py-1.5 text-[10px] uppercase font-semibold"
              style={{ color: tab === o.k ? C.primary : C.slateMuted,
                        borderBottom: tab === o.k ? `2px solid ${C.primary}` : 'none' }}>
              {o.l}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-2 mb-2">
          <TextInput label="Feature property → location code" value={nameField} onChange={setNameField}
            testid="gis-name-field" placeholder="name" />
          <TextInput label="Feature property → label" value={labelField} onChange={setLabelField}
            testid="gis-label-field" placeholder="name" />
        </div>
        {tab === 'shp' && (
          <div>
            <div className="text-[10px] mb-2" style={{ color: C.slateMuted }}>
              Select <b>.shp + .dbf (+ optional .shx)</b> together. Server parses via pyshp — no client-side conversion required.
            </div>
            <input type="file" multiple accept=".shp,.dbf,.shx"
              onChange={e => onShpFiles(e.target.files)}
              data-testid="gis-shp-input"
              className="text-[10px] mb-2 block" />
            <div className="grid grid-cols-3 gap-2 text-[10px] mb-2">
              <div className={`p-1.5 rounded border text-center ${shp.shp ? 'border-green-400 bg-green-50' : ''}`}
                style={{ borderColor: shp.shp ? undefined : C.border }}>
                .shp {shp.shp ? '✓' : '—'}
              </div>
              <div className={`p-1.5 rounded border text-center ${shp.dbf ? 'border-green-400 bg-green-50' : ''}`}
                style={{ borderColor: shp.dbf ? undefined : C.border }}>
                .dbf {shp.dbf ? '✓' : '—'}
              </div>
              <div className={`p-1.5 rounded border text-center ${shp.shx ? 'border-green-400 bg-green-50' : 'opacity-70'}`}
                style={{ borderColor: shp.shx ? undefined : C.border }}>
                .shx {shp.shx ? '✓' : 'optional'}
              </div>
            </div>
          </div>
        )}
        {tab === 'geojson' && (
          <div>
            <div className="text-[10px] mb-2" style={{ color: C.slateMuted }}>
              Paste a <b>GeoJSON FeatureCollection</b>. Points → pins, Polygons → centroid.
            </div>
            <input type="file" accept=".geojson,.json" onChange={e => onFile(e.target.files?.[0])}
              data-testid="gis-file-input" className="text-[10px] mb-2 block" />
            <textarea value={text} onChange={e => setText(e.target.value)}
              data-testid="gis-payload"
              placeholder='{"type":"FeatureCollection","features":[...]}'
              rows={7} className="w-full text-[10px] font-mono border rounded p-2"
              style={{ borderColor: C.border }} />
          </div>
        )}
        <div className="flex justify-end gap-2 mt-2">
          <Button size="sm" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={submit} disabled={busy}
            data-testid="gis-submit"
            style={{ background: C.primary, color: '#fff' }}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Import'}
          </Button>
        </div>
      </div>
    </div>
  );
}
